import { useEffect, useState, type FormEvent } from "react";
import { navLinks, siteMeta } from "@/mocks/home";

interface SiteNavProps {
  notify: (message: string) => void;
}

export default function SiteNav({ notify }: SiteNavProps) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    notify("搜索功能将在正式版开放，先看看下面的入口列表吧");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/90 backdrop-blur-md border-b border-background-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="flex items-center justify-between gap-4 px-4 md:px-10 h-16 md:h-[72px]">
        <a href="#top" className="flex items-center gap-2.5 cursor-pointer shrink-0">
          <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center text-background-50">
            <i className="ri-fire-fill text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold text-foreground-950">
              {siteMeta.name}
            </span>
            <span className="text-[10px] tracking-wider text-foreground-500">
              {siteMeta.tagline}
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="px-3.5 py-2 rounded-md text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="hidden lg:flex items-center gap-2 h-10 px-3 rounded-md bg-background-100 border border-background-200 focus-within:border-primary-300 transition-colors">
            <i className="ri-search-line text-foreground-400 text-base" />
            <input
              type="text"
              name="keyword"
              placeholder="搜索入口 / 话题"
              className="w-40 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
            />
          </form>
          <a
            href="#entries"
            className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-links-line" />
            获取入口
          </a>
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            aria-label="展开菜单"
            className="md:hidden w-10 h-10 rounded-md flex items-center justify-center text-foreground-800 bg-background-100 border border-background-200 cursor-pointer"
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {open ? (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-3 flex flex-col gap-1 animate-fade-in">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#subscribe"
            onClick={() => setOpen(false)}
            className="mt-1 px-3 py-2.5 rounded-md text-sm font-medium text-center bg-primary-500 text-background-50 cursor-pointer"
          >
            订阅更新通知
          </a>
        </div>
      ) : null}
    </header>
  );
}